<template>
  <available-section section="messengers">
    <main class="integration-chat-push">
      <modal-dialog
        ref="iframeModal"
        v-if="shownIframeAuthorizeModal"
        content-class="integration-messenger-modal"
        title=""
        v-bind:show-title="false"
        v-model="shownIframeAuthorizeModal"
        v-on:input="onCloseIframeAuthorizeModal"
        with-cross
      >
        <div class="integration-messenger-modal__content" ref="iframeContainer">
          <iframe
            v-if="authLink"
            class="integration-messenger-modal__frame"
            ref="iframe"
            v-bind:title="messengerName"
            v-bind:src="authLink"
          />
          <p v-if="messengerIsAuthenticated" class="integration-messenger-modal__text">
            Вы успешно авторизованы. Перейдите к&nbsp;настройке
            <router-link
              class="integration-chat-push__link"
              v-bind:to="{ name: 'ClinicPanelNotificationPlans' }">
              рассылок
            </router-link>
          </p>
        </div>
      </modal-dialog>
      <div class="integration-chat-push__container">
        <button-back />
        <h1 class="integration-chat-push__title">Мессенджеры</h1>
        <h4 class="integration-chat-push__subtitle">Выберите сервисы и пройдите авторизацию</h4>
        <div class="integration-chat-push__container">
          <div class="integration-chat-push__items">
            <button
              class="integration-chat-push__item"
              type='button'
              v-for="{
                type,
                name,
                status,
                index,
              } in messengers"
              v-bind:key="index"
              v-on:click="onMessengerAuthorize(type)"
            >
              <span class="integration-chat-push__item__caption">{{ name }}</span>
              <span
                v-if="status"
                class="integration-chat-push__item__description"
              >
                {{ status }}
              </span>
            </button>
          </div>
        </div>
        <support-contacts class="integration-chat-push__contacts" title="Если у вас возникли вопросы, свяжитесь с нами:" />
      </div>
      <modal-dialog
        class="integration-messenger-modal"
        v-bind:title="`Авторизация в мессенджере ${messengerName}`"
        width="100%"
        max-width="500px"
        v-model="shownDialogFormMax"
        v-on:input="onCloseMaxDialog"
        with-cross
      >
        <div class="integration-messenger-modal__content">
          <template v-if="!messengerIsAuthenticated">
            <div class="integration-chat-push__qr-container" v-if="qrCode">
              <img :src="`data:image/png;base64,${qrCode}`" alt="QR Code" class="integration-chat-push__qr-code" />
              <p class="integration-messenger-modal__text">Отсканируйте QR-код в приложении MAX</p>
            </div>
            <div v-else class="integration-chat-push__loader">
              <p class="integration-messenger-modal__text">Ожидание QR-кода...</p>
            </div>
            <div class="integration-chat-push__buttons">
              <button
                class="integration-chat-push__button-cancel"
                type="button"
                v-on:click="onCloseMaxDialog"
              >Отмена</button>
            </div>
          </template>
          <template v-else>
            <p class="integration-messenger-modal__text">
              Вы успешно авторизованы в MAX. Перейдите к&nbsp;настройке
              <router-link
                class="integration-chat-push__link"
                v-bind:to="{ name: 'ClinicPanelNotificationPlans' }">
                рассылок
              </router-link>
            </p>
            <div class="integration-chat-push__buttons">
              <button
                class="integration-chat-push__button-cancel"
                type="button"
                v-on:click="onResetMaxAuth"
              >Разлогинить</button>
              <button
                class="integration-chat-push__button-cancel"
                type="button"
                v-on:click="onCloseMaxDialog"
              >Закрыть</button>
            </div>
          </template>
        </div>
      </modal-dialog>
    </main>
  </available-section>
</template>
<script>
import clipboardMixin from '@/mixins/clipboardMixin';
import notifierMixin from '@/mixins/notifierMixin';
import SupportContacts from '@/components/SupportContacts.vue';
import AvailableSection from '@/views/ClinicPanel/AvailableSection.vue';
import ButtonBack from '@/components/ButtonBack.vue';
import ModalDialog from '@/components/ModalDialog.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Примесь авторизации в МАКС.
 * @type {Vue}
 */
const maxAuthMixin = {
  data() {
    return {
      qrCode: null,
      socket: null,
      channel: null,
      heartbeatInterval: null,
      isResetting: false,
    };
  },
  beforeDestroy() {
    this.disconnectSocket();
  },
  methods: {
    /**
     * @description Открыть диалоговое окно формы авторизации в MAX.
     * @returns {void}
     */
    openMaxFormDialog() {
      this.shownDialogFormMax = true;
      if (!this.messengerIsAuthenticated) {
        this.initMaxAuth();
      }
    },
    /**
     * @description Закрыть диалоговое окно MAX и отключить сокет.
     * @returns {void}
     */
    onCloseMaxDialog() {
      this.shownDialogFormMax = false;
      this.disconnectSocket();
      this.qrCode = null;
      this.isResetting = false;
      this.fetchMessengers();
    },
    /**
     * @description Инициализация авторизации MAX через WebSocket.
     * @returns {void}
     */
    initMaxAuth() {
      const onFail = ({ title }) => { this.notifyFail(title); };
      const onSuccess = (response) => {
        this.connectToSocket(response.token);
      };
      this.$messengersIntegrationGateway.getMaxAuthToken()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Подключение к WebSocket.
     * @param {string} token
     * @returns {void}
     */
    connectToSocket(token) {
      const socketUrl = `wss://api.chatpush.ru/socket/websocket?token=${token}&vsn=2.0.0`;
      this.socket = new WebSocket(socketUrl);
      this.socket.onopen = () => {
        this.joinChannel();
        this.startHeartbeat();
      };
      this.socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        this.handleSocketMessage(data);
      };
      this.socket.onerror = () => {
        this.notifyFail('Ошибка подключения к серверу авторизации');
      };
      this.socket.onclose = () => {
        this.stopHeartbeat();
      };
    },
    /**
     * @description Подключение к каналу авторизации.
     * @returns {void}
     */
    joinChannel() {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        const joinMessage = ['1', '1', 'room:v1:max:auth', 'phx_join', {}];
        this.socket.send(JSON.stringify(joinMessage));
      }
    },
    /**
     * @description Обработка сообщений от WebSocket.
     * @param {object} data
     * @returns {void}
     */
    handleSocketMessage(data) {
      const event = data[3];
      const payload = data[4];
      if (event === 'auth_state') {
        if (payload.status === 'wait_qr_code') {
          this.qrCode = payload.qr_code;
        } else if (payload.status === 'authorized') {
          if (this.isResetting) {
            return;
          }
          this.qrCode = null;
          this.notifySuccess('Авторизация успешна');
          this.disconnectSocket();
          this.updateAuthStatus(2);
        }
      } else if (event === 'phx_reply') {
        if (payload.status === 'ok') {
          if (this.isResetting) {
            this.notifySuccess('Авторизация сброшена');
            this.disconnectSocket();
            this.isResetting = false;
            this.updateAuthStatus(0);
          }
        } else if (payload.status === 'error') {
          this.notifyFail(payload.response?.meta?.message || 'Ошибка');
          this.isResetting = false;
        }
      }
    },
    /**
     * @description Отключение от WebSocket.
     * @returns {void}
     */
    disconnectSocket() {
      if (this.socket) {
        this.socket.close();
        this.socket = null;
      }
      this.stopHeartbeat();
    },
    /**
     * @description Запуск heartbeat.
     * @returns {void}
     */
    startHeartbeat() {
      this.heartbeatInterval = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify([null, '1', 'phoenix', 'heartbeat', {}]));
        }
      }, 5000);
    },
    /**
     * @description Остановить heartbeat
     * @returns {void}
     */
    stopHeartbeat() {
      if (this.heartbeatInterval) {
        clearInterval(this.heartbeatInterval);
        this.heartbeatInterval = null;
      }
    },
    /**
     * @description Сброс авторизации MAX через WebSocket.
     * @returns {void}
     */
    onResetMaxAuth() {
      this.isResetting = true;
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        this.sendResetCommand();
      } else {
        const onFail = ({ title }) => {
          this.notifyFail(title);
          this.isResetting = false;
        };
        const onSuccess = (response) => {
          this.connectToSocketForReset(response.token);
        };
        this.$messengersIntegrationGateway.getMaxAuthToken()
          .then((result) => result.fold(onFail, onSuccess));
      }
    },
    /**
     * @description Подключение к сокету специально для сброса.
     * @returns {void}
     */
    connectToSocketForReset(token) {
      const socketUrl = `wss://api.chatpush.ru/socket/websocket?token=${token}&vsn=2.0.0`;
      this.socket = new WebSocket(socketUrl);
      this.socket.onopen = () => {
        const joinMessage = ['1', '1', 'room:v1:max:auth', 'phx_join', {}];
        this.socket.send(JSON.stringify(joinMessage));
      };
      this.socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        const eventName = data[3];
        const payload = data[4];
        if (eventName === 'phx_reply' && payload.status === 'ok') {
          if (this.isResetting && !this.resetSent) {
            this.sendResetCommand();
            this.resetSent = true;
            return;
          }
          if (this.isResetting && this.resetSent) {
            this.notifySuccess('Авторизация сброшена');
            this.disconnectSocket();
            this.isResetting = false;
            this.resetSent = false;
            this.updateAuthStatus(0);
            return;
          }
        }
        this.handleSocketMessage(data);
      };
      this.socket.onerror = () => {
        this.notifyFail('Ошибка подключения для сброса');
        this.isResetting = false;
      };
    },
    /**
     * @description Отправить команду сброса авторизации.
     * @returns {void}
     */
    sendResetCommand() {
      const resetMessage = ['1', '1', 'room:v1:max:auth', 'reset', {}];
      this.socket.send(JSON.stringify(resetMessage));
    },
    /**
     * @description Обновить статус авторизации на бэкенде.
     * @param {number} status
     * @returns {void}
     */
    updateAuthStatus(status) {
      const onFail = ({ title }) => { this.notifyFail(title); };
      const onSuccess = () => {
        this.fetchMessengers();
        if (status === 0) {
          this.initMaxAuth();
        } else {
          this.shownDialogFormMax = false;
        }
      };
      this.$messengersIntegrationGateway.updateMaxAuthStatus(status)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
};

/**
 * @description Компонент интеграции с мессенджерами.
 * @type {Vue}
 */
export default {
  messengerMax: 'max',
  components: {
    ModalDialog,
    ButtonBack,
    AvailableSection,
    SupportContacts,
  },
  computed: {
    phoneNumberInputErrors: resolveInputErrors('phone'),
    /**
     * @description Наименование мессенджера
     * @returns {string}
     */
    messengerName() {
      return this
        .messengers
        .find((messenger) => messenger.type === this.selectedMessenger)
        ?.name ?? 'Название отсутствует';
    },
    /**
     * @description Мессенджер авторизован
     * @returns {boolean}
     */
    messengerIsAuthenticated() {
      return this
        .messengers
        .find((messenger) => messenger.type === this.selectedMessenger)
        ?.is_authenticated ?? false;
    },
  },
  created() {
    this.fetchMessengers();
  },
  data() {
    return {
      authLink: '',
      messengers: [],
      selectedMessenger: '',
      shownIframeAuthorizeModal: false,
      shownDialogFormMax: false,
      inputErrors: [],
      isAwaitRequest: false,
    };
  },
  methods: {
    /**
     * @description Закрыть модельное окно Iframe авторизации.
     * @returns {void}
     */
    onCloseIframeAuthorizeModal() {
      this.shownIframeAuthorizeModal = false;
      this.authLink = '';
      this.selectedMessenger = '';
      this.fetchMessengers();
    },
    /**
     * @description Получить список мессенджеров.
     * @returns {void}
     */
    fetchMessengers() {
      const onFail = ({ title }) => { this.notifyFail(title); };
      const onSuccess = (response) => {
        this.messengers = response.messengers;
      };
      this
        .$messengersIntegrationGateway
        .getMessengersList()
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать авторизацию в мессенджере.
     * @param {string} type
     * @returns {void}
     */
    onMessengerAuthorize(type) {
      this.selectedMessenger = type;
      if (type === this.$options.messengerMax) {
        this.openMaxFormDialog();
      } else {
        this.getIframeLinkByType(type);
      }
    },
    /**
     * @description Получить ссылку на Iframe-авторизации по типу мессенджера.
     * @param {string} type
     */
    getIframeLinkByType(type) {
      const onFail = ({ title }) => { this.notifyFail(title); };
      const onSuccess = (response) => {
        this.authLink = response.link;
        this.shownIframeAuthorizeModal = true;
        this.selectedMessenger = type;
      };
      this
        .$messengersIntegrationGateway
        .getIframeLink(type)
        .then((result) => result.fold(onFail, onSuccess));
    },
  },
  mixins: [
    clipboardMixin,
    notifierMixin,
    maxAuthMixin,
  ],
  name: 'ClinicPanelIntegrationChatPush',
};
</script>
<style lang="scss">
@use "@/scss/copy-icon";
@use "@/scss/buttons";

.integration-messenger-modal {
   &__content {
    display: grid;
    row-gap: 20px;
  }
  &__text {
    display: block !important;
    font-size: 16px;
    color: var(--reading-color);
  }
  &__frame {
    width: 100%;
    height: 550px;
    border: none;
  }
}

.integration-chat-push {
  width: 100%;
  height: 100%;
  overflow: scroll;
  &__container {
    padding: 38px 20px 200px 23px;
  }
  &__title {
    font-weight: 600;
    font-size: 38px;
    line-height: 46px;
    color: #000000;
    margin: 0 0 22px 0;
  }
  &__items {
    margin-top: 20px;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 350px));
    grid-gap: 12px;
  }
  &__item {
    @include buttons.btn-transparent(100%, 100px, 18px);
    flex-direction: column;
    gap: 10px;
    padding: 0 5px;
    text-align: center;
    text-decoration: none;
    &__caption {
      font-weight: 700;
    }
    &__description {
      font-size: 1rem;
      color: #707683;
    }
  }
  &__subtitle {
    display: block !important;
    font-weight: 400;
    font-size: 16px;
    line-height: 28px;
    a:hover {
      text-decoration: none;
    }
  }
  &__field-group {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 10px;
  }
  &__buttons {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    justify-items: center;
    margin: 20px 0 0 auto;
    max-width: 438px;
    width: 100%;
  }
  &__button-cancel {
    @include buttons.btn-transparent(100%, 3rem, 16px);
    max-width: 214px;
  }
  &__button-submit {
    @include buttons.btn-blue(100%, 3rem, 16px);
    max-width: 214px;
  }
  &__qr-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
  }
  &__qr-code {
      width: 250px;
      height: 250px;
  }
  &__loader {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 200px;
  }
}

@media screen and (max-width: 1240px) {
  .integration {
    &__container {
      width: 100%;
      padding: 45px 20px 145px;
    }
  }
}
@media screen and (max-width: 880px) {
  .integration-chat-push {
    &__items {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    }
    &__item:only-child {
      max-width: 350px;
    }
  }
}
@media screen and (max-width: 640px) {
  .integration {
    &__container {
      padding: 45px 10px 145px;
    }
  }
}
@media screen and (max-width: 600px) {
  .integration {
    &__table {
      &__head {
        display: none;
      }
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 10px;
        margin: 0 0 20px 0;
      }
      &__cell {
        display: flex;
        justify-content: space-between;
        text-align: right;
        font-size: 12px;
      }
      &__cell:before {
        content: attr(data-label);
        font-weight: 700;
        margin-right: 20px;
        font-size: 10px;
        text-align: left;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .integration {
    &__connection {
      background-color: transparent;
      padding: 0 0 20px 0;
    }
    &__control {
      padding: 20px 0 0 0;
      background-color: transparent;
    }
  }
  .integration-chat-push {
    &__items {
      grid-template-columns: 1fr;
    }
  }
}

@media screen and (max-width: 400px) {
  .integration-chat-push {
    &__field-group,
    &__buttons {
      grid-template-columns: 1fr;
    }
    &__button-cancel,
    &__button-submit {
      max-width: none;
    }
  }
}
</style>
